// Ai-POS · Impresora térmica USB de 80 mm en Android (WebUSB, Chrome).
// Mismo contrato que el agente de Windows (localAgent.js): trabajos escpos/raster + pulso de gaveta.
// Funciona con impresoras ESC/POS externas (Rongta, Epson, Xprinter, Bixolon, 3nStar…).
import { rasterBytesFromDataUrl } from "./btPrinter";

const KEY = "aipos_usb_printer";
let _dev = null;
let _ep = null;
let _iface = null;
let _epIn = null;
let _leyendo = null;        // lectura de estado en curso (WebUSB no permite cancelarla)
let _sinEstado = false;     // la impresora no responde DLE EOT (3 intentos): no se vuelve a consultar

export const isUsbSupported = () => typeof navigator !== "undefined" && !!navigator.usb;
export const usbSavedName = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null")?.name || null; } catch { return null; } };
export const usbStalled = () => !!_atascado;
export const isUsbConnected = () => !!(_dev && _dev.opened && _ep != null);

const _nombre = (d) => [d.manufacturerName, d.productName].filter(Boolean).join(" ") || `USB ${d.vendorId.toString(16)}:${d.productId.toString(16)}`;

// Interfaz de impresora (clase 7) o, si no hay, la primera con endpoint bulk OUT (clones genéricos).
function _findOut(dev) {
  const cands = [];
  for (const iface of dev.configuration?.interfaces || []) {
    for (const alt of iface.alternates) {
      const out = alt.endpoints.find((e) => e.direction === "out" && e.type === "bulk");
      const inp = alt.endpoints.find((e) => e.direction === "in" && e.type === "bulk");
      if (out) cands.push({ iface: iface.interfaceNumber, alt: alt.alternateSetting, ep: out.endpointNumber, epIn: inp ? inp.endpointNumber : null, printer: alt.interfaceClass === 7 });
    }
  }
  return cands.find((c) => c.printer) || cands[0] || null;
}

async function _open(dev) {
  if (!dev.opened) await dev.open();
  if (!dev.configuration) await dev.selectConfiguration(1);
  const c = _findOut(dev);
  if (!c) throw new Error("La impresora USB no tiene un canal de impresión compatible (ESC/POS).");
  try { await dev.claimInterface(c.iface); } catch (e) {
    throw new Error("Android no liberó la impresora USB. Desconéctala y vuelve a conectarla, y acepta el permiso.");
  }
  if (c.alt) { try { await dev.selectAlternateInterface(c.iface, c.alt); } catch { /* opcional */ } }
  _dev = dev; _ep = c.ep; _iface = c.iface; _epIn = c.epIn; _sinEstado = false; _leyendo = null; _fallosDle = 0;
  try { localStorage.setItem(KEY, JSON.stringify({ name: _nombre(dev), vendorId: dev.vendorId, productId: dev.productId })); } catch { /* ignore */ }
}

/** Selector del navegador (requiere un toque del usuario). Devuelve el nombre de la impresora. */
export async function usbPair() {
  if (!isUsbSupported()) throw new Error("Este navegador no permite USB directo. Usa Google Chrome en Android.");
  const dev = await navigator.usb.requestDevice({ filters: [] });
  await _open(dev);
  return _nombre(dev);
}

/** Reconecta sin preguntar con la impresora ya autorizada (permiso guardado por Chrome). */
export async function usbReconnect() {
  if (!isUsbSupported()) return false;
  if (isUsbConnected()) return true;
  try {
    const devs = await navigator.usb.getDevices();
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(KEY) || "null"); } catch { /* ignore */ }
    const dev = (saved && devs.find((d) => d.vendorId === saved.vendorId && d.productId === saved.productId)) || (devs.length === 1 ? devs[0] : null);
    if (!dev) return false;
    await _open(dev);
    return true;
  } catch { return false; }
}

export function usbForget() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  const d = _dev; _dev = null; _ep = null;
  if (d) { d.close().catch(() => {}); d.forget?.().catch?.(() => {}); }
}

if (isUsbSupported()) {
  navigator.usb.addEventListener?.("disconnect", (e) => { if (e.device === _dev) { _dev = null; _ep = null; } });
  navigator.usb.addEventListener?.("connect", () => { usbReconnect(); });
}

// Las escrituras y las consultas de estado van en fila: un DLE EOT a mitad de un raster lo corrompería.
let _cola = Promise.resolve();
const _serial = (fn) => { const p = _cola.then(() => fn()); _cola = p.catch(() => {}); return p; };

const _write = (bytes, ms) => _serial(() => _writeNow(bytes, ms));
let _atascado = null;   // envío detenido (tapa abierta / sin papel): no apilamos más trabajos detrás

async function _writeNow(bytes, ms = 10000) {
  if (_atascado) {
    const err = new Error("La impresora no responde: revisa la tapa y el papel. Luego usa «Reimprimir».");
    err.code = "USB_STALLED";
    throw err;
  }
  if (!isUsbConnected() && !(await usbReconnect())) {
    const err = new Error("La impresora USB no está conectada. Toca «Conectar impresora USB».");
    err.code = "USB_NEEDS_PAIRING";
    throw err;
  }
  const CH = 16384;
  for (let i = 0; i < bytes.length; i += CH) {
    // Si la impresora se detiene (tapa abierta / sin papel) el envío queda esperando: no bloqueamos la caja
    const tx = _dev.transferOut(_ep, bytes.subarray(i, i + CH));
    const r = await _timeout(tx, ms);
    if (r === undefined) {
      _atascado = tx;
      tx.finally(() => { if (_atascado === tx) _atascado = null; });
      const err = new Error("La impresora no responde: revisa la tapa y el papel. Luego usa «Reimprimir».");
      err.code = "USB_STALLED";
      throw err;
    }
    if (r.status !== "ok") throw new Error(`La impresora USB respondió ${r.status}`);
  }
}

const _b64 = (b64) => { const bin = atob(b64); const u = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i); return u; };

/** Pulso ESC p de la gaveta: pin 0 = pin 2 · 1 = pin 5. Tiempos en ms (unidades de 2 ms). */
export const drawerPulse = (pin = 0, onMs = 100, offMs = 500) =>
  new Uint8Array([0x1b, 0x70, pin ? 1 : 0, Math.min(255, Math.round(onMs / 2)), Math.min(255, Math.round(offMs / 2))]);

/** Imprime trabajos {type: "escpos"|"raster", data_base64} en orden (igual que el agente). */
export async function usbPrint(jobs, { feedBeforeCut, dots = 576 } = {}) {
  if (_atascado) { const err = new Error("La impresora no responde: revisa la tapa y el papel. Luego usa «Reimprimir»."); err.code = "USB_STALLED"; throw err; }
  const m = usbStatusMensaje(await usbStatus());
  if (m && m.nivel === "error") { const err = new Error(m.texto); err.code = "USB_NOT_READY"; throw err; }
  for (const j of jobs) {
    const bytes = j.type === "raster"
      ? await rasterBytesFromDataUrl(`data:image/png;base64,${j.data_base64}`, dots, { feedBeforeCut: feedBeforeCut ?? 4 })
      : _b64(j.data_base64);
    await _write(bytes);
  }
}

export async function usbOpenDrawer(pin = 0) { await _write(drawerPulse(pin), 3000); }

export async function usbTest(pin = 0, conGaveta = true) {
  const enc = new TextEncoder();
  const txt = enc.encode(`AI-POS\nPrueba de impresion USB 80 mm\n${usbSavedName() || ""}\n${new Date().toLocaleString("es-CL")}\n`);
  await _write(new Uint8Array([0x1b, 0x40, 0x1b, 0x61, 0x01, ...txt, 0x1b, 0x64, 0x04, 0x1d, 0x56, 0x00]));
  if (conGaveta) await usbOpenDrawer(pin);
}

const _estadoOk = (b) => (b & 0x93) === 0x12;  // bits fijos de la respuesta DLE EOT
const _timeout = (p, ms) => Promise.race([p, new Promise((res) => setTimeout(() => res(undefined), ms))]);
let _fallosDle = 0;

// Lee del endpoint IN hasta encontrar un byte de estado válido (descarta ASB/bytes viejos y lecturas vacías).
async function _leerEstado(ms) {
  const fin = Date.now() + ms;
  while (Date.now() < fin) {
    if (!_leyendo) {
      _leyendo = _dev.transferIn(_epIn, 64).then((r) => { _leyendo = null; return r.data ? new Uint8Array(r.data.buffer, r.data.byteOffset, r.data.byteLength) : new Uint8Array(); },
        () => { _leyendo = null; return null; });
    }
    const data = await _timeout(_leyendo, Math.max(50, fin - Date.now()));
    if (data === undefined || data === null) return null;
    for (let k = data.length - 1; k >= 0; k--) if (_estadoOk(data[k])) return data[k];
  }
  return null;
}

async function _consulta(n) {
  const w = await _timeout(_dev.transferOut(_ep, new Uint8Array([0x10, 0x04, n])), 900);
  if (w === undefined) return null;   // el canal de datos está bloqueado (tapa abierta en algunas Epson)
  return _leerEstado(900);
}

// Estado estándar de la clase impresora USB (GET_PORT_STATUS): funciona aunque la impresora esté "offline".
async function _portStatus() {
  try {
    const r = await _timeout(_dev.controlTransferIn({ requestType: "class", recipient: "interface", request: 1, value: 0, index: _iface }, 1), 800);
    if (!r || r.status !== "ok" || !r.data || !r.data.byteLength) return null;
    return r.data.getUint8(0);
  } catch { return null; }
}

/** Estado: tapa abierta, sin papel, poco papel. Usa GET_PORT_STATUS (USB) y DLE EOT 2/4 (ESC/POS).
 *  Devuelve {soportado:false} si la impresora no informa estado. */
export const usbStatus = async () => {
  if (!isUsbConnected()) return { conectada: false };
  const ps = await _portStatus();          // canal de control: no espera la fila de impresión
  const dle = await _timeout(_serial(_dleNow), 2500);
  const st = { conectada: true, soportado: false, tapaAbierta: false, sinPapel: false, pocoPapel: false, error: false, noLista: false };
  if (dle) Object.assign(st, dle, { soportado: true });
  if (ps != null) {
    st.soportado = true;
    if (ps & 0x20) st.sinPapel = true;
    // bit 3 = 0 → error (tapa abierta, atasco o sin papel según el modelo)
    if (!(ps & 0x08) && !st.tapaAbierta && !st.sinPapel && !st.error) st.noLista = true;
  }
  return st;
};

async function _dleNow() {
  if (!isUsbConnected() || _epIn == null || _sinEstado || _atascado) return null;
  try {
    const off = await _consulta(2);
    if (off == null) { if (++_fallosDle >= 3) _sinEstado = true; return null; }
    _fallosDle = 0;
    const rollo = await _consulta(4);
    return {
      tapaAbierta: !!(off & 0x04),
      sinPapel: !!(off & 0x20) || (rollo != null && (rollo & 0x60) === 0x60),
      pocoPapel: rollo != null && (rollo & 0x0c) === 0x0c,
      error: !!(off & 0x40),
    };
  } catch { return null; }
}

/** Texto para el cajero según el estado (null = todo bien). */
export function usbStatusMensaje(st) {
  if (!st?.soportado) return null;
  if (st.tapaAbierta) return { nivel: "error", texto: "La impresora tiene la tapa abierta. Ciérrala para seguir imprimiendo." };
  if (st.sinPapel) return { nivel: "error", texto: "La impresora se quedó sin papel. Cambia el rollo para seguir imprimiendo." };
  if (st.error) return { nivel: "error", texto: "La impresora tiene un error (revisa el cortador o apágala y enciéndela)." };
  if (st.noLista) return { nivel: "error", texto: "La impresora no está lista: revisa que la tapa esté cerrada y que tenga papel." };
  if (st.pocoPapel) return { nivel: "aviso", texto: "Queda poco papel en la impresora: ten un rollo a mano." };
  return null;
}
